import type { JSX } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { LoadingState, NotFoundState } from '../components';
import {
  CompanyDetailScreen,
  RefrigerationUnitDetailScreen,
  SiteDetailScreen,
} from '../features/companies';
import { ReportDetailLayout } from '../features/reports';
import { useCompany } from '../features/companies/hooks';
import { useReport } from '../features/reports/hooks';

export function CompanyDetailGuard(): JSX.Element {
  const params = useParams({ from: '/companies/$companyId' });
  const navigate = useNavigate({ from: '/companies/$companyId' });
  const { data: company, isLoading } = useCompany(params.companyId);

  if (isLoading) {
    return <LoadingState label="Caricamento scheda azienda" />;
  }

  if (!company) {
    return (
      <NotFoundState
        icon="corporate_fare"
        title="Azienda non trovata"
        description="La risorsa richiesta potrebbe essere stata eliminata oppure il link non è più valido."
        actionLabel="Torna alle aziende"
        onAction={() => navigate({ to: '/companies' })}
      />
    );
  }

  return <CompanyDetailScreen />;
}

export function SiteDetailGuard(): JSX.Element {
  const params = useParams({ from: '/companies/$companyId/sites/$siteId' });
  const navigate = useNavigate({
    from: '/companies/$companyId/sites/$siteId',
  });
  const { data: company, isLoading } = useCompany(params.companyId);
  const site = company?.sites.find((item) => item.id === params.siteId) ?? null;

  if (isLoading) {
    return <LoadingState label="Caricamento stabilimento" />;
  }

  if (!company) {
    return (
      <NotFoundState
        icon="corporate_fare"
        title="Azienda non trovata"
        description="Seleziona un'altra azienda per visualizzare gli stabilimenti disponibili."
        actionLabel="Torna alle aziende"
        onAction={() => navigate({ to: '/companies' })}
      />
    );
  }

  if (!site) {
    return (
      <NotFoundState
        icon="factory"
        title="Stabilimento non trovato"
        description="Controlla che il collegamento sia corretto oppure seleziona un altro stabilimento."
        actionLabel="Torna alla scheda azienda"
        onAction={() =>
          navigate({
            to: '/companies/$companyId',
            params: { companyId: company.id },
            search: { tab: undefined },
          })
        }
      />
    );
  }

  return <SiteDetailScreen />;
}

export function RefrigerationUnitDetailGuard(): JSX.Element {
  const params = useParams({
    from: '/companies/$companyId/sites/$siteId/units/$unitId',
  });
  const navigate = useNavigate({
    from: '/companies/$companyId/sites/$siteId/units/$unitId',
  });
  const { data: company, isLoading } = useCompany(params.companyId);
  const site = company?.sites.find((item) => item.id === params.siteId) ?? null;
  const unit = site?.refrigerationUnits?.find((item) => item.id === params.unitId) ?? null;

  if (isLoading) {
    return <LoadingState label="Caricamento impianto" />;
  }

  if (!company) {
    return (
      <NotFoundState
        icon="corporate_fare"
        title="Azienda non trovata"
        description="Seleziona un'altra azienda per visualizzare gli impianti disponibili."
        actionLabel="Torna alle aziende"
        onAction={() => navigate({ to: '/companies' })}
      />
    );
  }

  if (!site) {
    return (
      <NotFoundState
        icon="factory"
        title="Stabilimento non trovato"
        description="La risorsa richiesta non è più disponibile. Seleziona un altro stabilimento."
        actionLabel="Torna alla scheda azienda"
        onAction={() =>
          navigate({
            to: '/companies/$companyId',
            params: { companyId: company.id },
            search: { tab: undefined },
          })
        }
      />
    );
  }

  if (!unit) {
    return (
      <NotFoundState
        icon="ac_unit"
        title="Impianto non trovato"
        description="La risorsa richiesta potrebbe essere stata spostata o eliminata."
        actionLabel="Torna allo stabilimento"
        onAction={() =>
          navigate({
            to: '/companies/$companyId/sites/$siteId',
            params: { companyId: company.id, siteId: site.id },
            search: { tab: undefined },
          })
        }
      />
    );
  }

  return <RefrigerationUnitDetailScreen />;
}

export function ReportDetailGuard(): JSX.Element {
  const params = useParams({ from: '/reports/$reportId' });
  const navigate = useNavigate({ from: '/reports/$reportId' });
  const isNewReport = params.reportId === 'new';
  const {
    data: report,
    isLoading,
    isError,
    error,
  } = useReport(isNewReport ? undefined : params.reportId);

  if (isNewReport) {
    return <ReportDetailLayout />;
  }

  if (isLoading) {
    return <LoadingState label="Caricamento report" />;
  }

  if (isError || !report) {
    const message =
      error instanceof Error ? error.message : 'Il report richiesto non è disponibile.';
    return (
      <NotFoundState
        icon="description"
        title="Report non disponibile"
        description={message}
        actionLabel="Torna ai report"
        onAction={() => navigate({ to: '/reports', search: { query: undefined } })}
      />
    );
  }

  return <ReportDetailLayout />;
}
